import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getPublicAvailabilityLabel } from "@/lib/publicAvailability";

interface TimeBookingGridProps {
  availableTimes: Array<{
    time: string;
    isAvailable: boolean;
    unavailableReason?: 'full' | 'capacity' | 'hours' | 'incompatible' | 'none';
  }>;
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  disabled?: boolean;
  /** Custom message to display when no times are available (e.g. closure message) */
  closureMessage?: string;
}

export function TimeBookingGrid({
  availableTimes,
  selectedTime,
  onTimeSelect,
  disabled = false,
  closureMessage,
}: TimeBookingGridProps) {
  if (availableTimes.length === 0) {
    const message = closureMessage || 'Aucun horaire disponible pour cette date.';
    const hint = closureMessage ? null : 'Essayez une autre date ou contactez-nous directement.';
    return (
      <div className="text-sm bg-amber-50 dark:bg-amber-950/30 text-amber-600 dark:text-amber-400 p-4 rounded-lg font-medium whitespace-pre-line">
        😔 {message}
        {hint && (
          <>
            <br />
            <span className="text-xs mt-2 block">{hint}</span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
      {availableTimes.map(({ time, isAvailable, unavailableReason }) => {
        const isSelected = selectedTime === time;
        const unavailableLabel = getPublicAvailabilityLabel(
          unavailableReason && unavailableReason !== 'none' ? unavailableReason : 'incompatible',
        );
        
        return (
          <Button
            key={time}
            type="button"
            variant={isSelected ? "default" : "outline"}
            size="sm"
            onClick={() => isAvailable && onTimeSelect(time)}
            disabled={disabled || !isAvailable}
            className={cn(
              "h-auto min-h-12 py-2 px-2 text-sm font-medium transition-all flex-col gap-0.5 whitespace-normal",
              isSelected && "ring-2 ring-primary ring-offset-2",
              !isAvailable && "opacity-50 cursor-not-allowed",
              !isAvailable && unavailableReason === 'full' && "line-through",
            )}
            title={!isAvailable ? unavailableLabel : undefined}
          >
            <span>{time}</span>
            {!isAvailable && (
              <span className="text-[11px] leading-tight font-normal">
                {unavailableLabel}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}
